// Sample address activity for the /map-prospector demo/empty state. Shape
// mirrors the /map-prospector response (`{ addresses: [...] }`) so the page's
// clustering, side-panel timeline and filters render unchanged. Every address
// carries a realistic latitude/longitude around the Manly / Northern Beaches
// area of Sydney so the map clusters convincingly. While these render,
// useDemoNetworkGuard keeps window.__demoMode on and nothing here touches the
// backend.

const iso = (daysAgo, hour = 10) => {
  const d = new Date(Date.now() - daysAgo * 86400 * 1000);
  d.setHours(hour, (daysAgo * 7) % 60, 0, 0);
  return d.toISOString();
};

// email event: shows in panel with subject + campaign tag
const mail = (event_type, email, subject, tag, daysAgo, extra = {}) => ({
  event_type,
  email,
  subject,
  tag,
  is_platform: false,
  message_id: `demo-msg-${Math.random().toString(36).slice(2, 10)}`,
  created_at: iso(daysAgo),
  ...extra,
});

// platform event: a website action against the property
const plat = (event_type, email, daysAgo, extra = {}) => ({
  event_type,
  email,
  is_platform: true,
  created_at: iso(daysAgo),
  ...extra,
});

const DEMO_MAP_ADDRESSES = [
  {
    property_id: "demo-mp-1",
    address: "12 Bower Street, Manly NSW 2095",
    lat: -33.79984,
    lng: 151.29062,
    events: [
      plat("property_analysed", "emma.thompson@example.com", 1, { phone: "0412 345 678" }),
      mail("Open", "emma.thompson@example.com", "Your appraisal for 12 Bower Street", "appraisal_send", 1),
      mail("Click", "emma.thompson@example.com", "Your appraisal for 12 Bower Street", "appraisal_send", 1, {
        clicked_url: "https://insights.exampleagency.com/appraisal/12-bower-st",
        country: "Australia",
      }),
      plat("click_track_property", "emma.thompson@example.com", 3),
    ],
  },
  {
    property_id: "demo-mp-2",
    address: "7 Darley Road, Manly NSW 2095",
    lat: -33.79742,
    lng: 151.28447,
    events: [
      plat("property_analysed", "liam.oconnor@example.com", 2, { phone: "0423 887 210" }),
      plat("view_appraisal", "liam.oconnor@example.com", 2),
      mail("Open", "liam.oconnor@example.com", "Manly market update — autumn 2026", "market_update", 5),
    ],
  },
  {
    property_id: "demo-mp-3",
    address: "29 Addison Road, Manly NSW 2095",
    lat: -33.80123,
    lng: 151.28765,
    events: [
      mail("Delivery", "sophia.chen@example.com", "New listings near Addison Road", "property_update", 4),
      mail("Open", "sophia.chen@example.com", "New listings near Addison Road", "property_update", 4),
      mail("Click", "sophia.chen@example.com", "New listings near Addison Road", "property_update", 4, {
        clicked_url: "https://insights.exampleagency.com/property/29-addison-rd",
        country: "Australia",
      }),
    ],
  },
  {
    property_id: "demo-mp-4",
    address: "3 Wentworth Street, Manly NSW 2095",
    lat: -33.79865,
    lng: 151.28932,
    events: [
      plat("property_analysed", "noah.williams@example.com", 6, { phone: "0438 552 901" }),
      plat("change_bedrooms", "noah.williams@example.com", 6),
      plat("send_property_report", "noah.williams@example.com", 6),
      mail("Open", "noah.williams@example.com", "Your property report for 3 Wentworth Street", "property_report", 6),
    ],
  },
  {
    property_id: "demo-mp-5",
    address: "44 Lauderdale Avenue, Fairlight NSW 2094",
    lat: -33.79366,
    lng: 151.27718,
    events: [
      plat("property_analysed", "olivia.martin@example.com", 8, { phone: "0401 223 845" }),
      mail("Open", "olivia.martin@example.com", "Fairlight market update", "market_update", 7),
      plat("click_share_valuation", "olivia.martin@example.com", 9),
    ],
  },
  {
    property_id: "demo-mp-6",
    address: "16 Sydney Road, Fairlight NSW 2094",
    lat: -33.79512,
    lng: 151.27894,
    events: [
      mail("Open", "jack.robinson@example.com", "Your appraisal for 16 Sydney Road", "appraisal_send", 3),
      plat("view_appraisal", "jack.robinson@example.com", 3, { phone: "0455 909 112" }),
    ],
  },
  {
    property_id: "demo-mp-7",
    address: "8 Lewis Street, Fairlight NSW 2094",
    lat: -33.79238,
    lng: 151.27556,
    events: [
      plat("property_analysed", "charlotte.lee@example.com", 11),
      plat("analysis_started", "charlotte.lee@example.com", 11),
    ],
  },
  {
    property_id: "demo-mp-8",
    address: "21 Condamine Street, Balgowlah NSW 2093",
    lat: -33.79284,
    lng: 151.26142,
    events: [
      plat("property_analysed", "william.brown@example.com", 5, { phone: "0466 332 778" }),
      plat("send_property_report", "william.brown@example.com", 5),
      mail("Open", "william.brown@example.com", "Your property report for 21 Condamine Street", "property_report", 5),
      mail("Click", "william.brown@example.com", "Your property report for 21 Condamine Street", "property_report", 4, {
        clicked_url: "https://insights.exampleagency.com/report/21-condamine-st",
        country: "Australia",
      }),
    ],
  },
  {
    property_id: "demo-mp-9",
    address: "5 White Street, Balgowlah NSW 2093",
    lat: -33.79401,
    lng: 151.26318,
    events: [
      mail("Open", "mia.taylor@example.com", "New listings in Balgowlah", "property_update", 9),
      plat("click_analyse_property", "mia.taylor@example.com", 9),
    ],
  },
  {
    property_id: "demo-mp-10",
    address: "33 Ethel Street, Balgowlah NSW 2093",
    lat: -33.79068,
    lng: 151.25987,
    events: [
      plat("property_analysed", "ethan.walker@example.com", 14, { phone: "0477 884 220" }),
      plat("view_appraisal", "ethan.walker@example.com", 14),
      mail("Open", "ethan.walker@example.com", "Balgowlah market update", "market_update", 12),
    ],
  },
  {
    property_id: "demo-mp-11",
    address: "9 Battle Boulevard, Seaforth NSW 2092",
    lat: -33.79842,
    lng: 151.24813,
    events: [
      plat("property_analysed", "ava.nguyen@example.com", 4, { phone: "0488 110 553" }),
      plat("change_land_area", "ava.nguyen@example.com", 4),
      mail("Open", "ava.nguyen@example.com", "Your appraisal for 9 Battle Boulevard", "appraisal_send", 4),
    ],
  },
  {
    property_id: "demo-mp-12",
    address: "18 Kirkwood Street, Seaforth NSW 2092",
    lat: -33.79965,
    lng: 151.24612,
    events: [
      mail("Delivery", "lucas.kelly@example.com", "Seaforth market update", "market_update", 6),
      mail("Open", "lucas.kelly@example.com", "Seaforth market update", "market_update", 6),
    ],
  },
  {
    property_id: "demo-mp-13",
    address: "2 Frenchs Forest Road, Seaforth NSW 2092",
    lat: -33.79588,
    lng: 151.24987,
    events: [
      plat("property_analysed", "grace.foster@example.com", 18),
      plat("click_track_property", "grace.foster@example.com", 18, { phone: "0401 776 332" }),
    ],
  },
  {
    property_id: "demo-mp-14",
    address: "27 Lawrence Street, Freshwater NSW 2096",
    lat: -33.77912,
    lng: 151.28734,
    events: [
      plat("property_analysed", "henry.scott@example.com", 2, { phone: "0413 559 028" }),
      plat("send_property_report", "henry.scott@example.com", 2),
      mail("Open", "henry.scott@example.com", "Your property report for 27 Lawrence Street", "property_report", 2),
      mail("Click", "henry.scott@example.com", "Your property report for 27 Lawrence Street", "property_report", 1, {
        clicked_url: "https://insights.exampleagency.com/report/27-lawrence-st",
        country: "Australia",
      }),
      plat("click_share_valuation", "henry.scott@example.com", 1),
    ],
  },
  {
    property_id: "demo-mp-15",
    address: "11 Soldiers Avenue, Freshwater NSW 2096",
    lat: -33.78045,
    lng: 151.28521,
    events: [
      mail("Open", "isla.murphy@example.com", "New listings in Freshwater", "property_update", 7),
      mail("Click", "isla.murphy@example.com", "New listings in Freshwater", "property_update", 7, {
        clicked_url: "https://insights.exampleagency.com/property/11-soldiers-ave",
        country: "Australia",
      }),
      plat("property_analysed", "isla.murphy@example.com", 6, { phone: "0424 880 117" }),
    ],
  },
  {
    property_id: "demo-mp-16",
    address: "6 Oliver Street, Freshwater NSW 2096",
    lat: -33.77788,
    lng: 151.28902,
    events: [
      plat("property_analysed", "oscar.bell@example.com", 21),
      plat("analysis_started", "oscar.bell@example.com", 21),
    ],
  },
  {
    property_id: "demo-mp-17",
    address: "14 Pavilion Street, Queenscliff NSW 2096",
    lat: -33.78624,
    lng: 151.28815,
    events: [
      plat("property_analysed", "ruby.cox@example.com", 10, { phone: "0432 117 905" }),
      plat("view_appraisal", "ruby.cox@example.com", 10),
      mail("Open", "ruby.cox@example.com", "Your appraisal for 14 Pavilion Street", "appraisal_send", 9),
    ],
  },
  {
    property_id: "demo-mp-18",
    address: "30 Crown Road, Queenscliff NSW 2096",
    lat: -33.78451,
    lng: 151.28673,
    events: [
      mail("Open", "leo.ward@example.com", "Queenscliff market update", "market_update", 13),
      plat("click_analyse_property", "leo.ward@example.com", 12),
    ],
  },
  {
    property_id: "demo-mp-19",
    address: "4 Howard Avenue, Dee Why NSW 2099",
    lat: -33.75184,
    lng: 151.29742,
    events: [
      plat("property_analysed", "zoe.hughes@example.com", 3, { phone: "0419 663 240" }),
      plat("change_bathrooms", "zoe.hughes@example.com", 3),
      plat("send_property_report", "zoe.hughes@example.com", 3),
      mail("Open", "zoe.hughes@example.com", "Your property report for 4 Howard Avenue", "property_report", 2),
    ],
  },
  {
    property_id: "demo-mp-20",
    address: "22 Oaks Avenue, Dee Why NSW 2099",
    lat: -33.75321,
    lng: 151.29588,
    events: [
      mail("Open", "max.griffin@example.com", "New listings in Dee Why", "property_update", 8),
      plat("property_analysed", "max.griffin@example.com", 7, { phone: "0426 559 871" }),
      plat("click_track_property", "max.griffin@example.com", 7),
    ],
  },
  {
    property_id: "demo-mp-21",
    address: "9 Carrington Parade, Curl Curl NSW 2096",
    lat: -33.76982,
    lng: 151.29521,
    events: [
      plat("property_analysed", "amelia.reed@example.com", 16, { phone: "0438 220 664" }),
      mail("Open", "amelia.reed@example.com", "Curl Curl market update", "market_update", 15),
    ],
  },
  {
    property_id: "demo-mp-22",
    address: "17 Wyadra Avenue, Freshwater NSW 2096",
    lat: -33.77654,
    lng: 151.28342,
    events: [
      plat("property_analysed", "felix.barnes@example.com", 5),
      plat("view_appraisal", "felix.barnes@example.com", 5, { phone: "0402 991 338" }),
      mail("Open", "felix.barnes@example.com", "Your appraisal for 17 Wyadra Avenue", "appraisal_send", 4),
    ],
  },
  {
    property_id: "demo-mp-23",
    address: "8 Raglan Street, Manly NSW 2095",
    lat: -33.79628,
    lng: 151.28556,
    events: [
      mail("Open", "harper.dixon@example.com", "Your appraisal for 8 Raglan Street", "appraisal_send", 1),
      mail("Click", "harper.dixon@example.com", "Your appraisal for 8 Raglan Street", "appraisal_send", 1, {
        clicked_url: "https://insights.exampleagency.com/appraisal/8-raglan-st",
        country: "Australia",
      }),
      plat("property_analysed", "harper.dixon@example.com", 1, { phone: "0410 882 047" }),
    ],
  },
  {
    property_id: "demo-mp-24",
    address: "25 Pittwater Road, Manly NSW 2095",
    lat: -33.79456,
    lng: 151.28298,
    events: [
      plat("property_analysed", "theo.morris@example.com", 9),
      plat("analysis_started", "theo.morris@example.com", 9, { phone: "0421 447 905" }),
      plat("save_notification_settings", "theo.morris@example.com", 9),
    ],
  },
  {
    property_id: "demo-mp-25",
    address: "13 Victoria Parade, Manly NSW 2095",
    lat: -33.79812,
    lng: 151.28701,
    events: [
      plat("property_analysed", "ivy.chapman@example.com", 12, { phone: "0433 778 220" }),
      plat("view_appraisal", "ivy.chapman@example.com", 12),
      mail("Open", "ivy.chapman@example.com", "Manly market update — autumn 2026", "market_update", 11),
      mail("Click", "ivy.chapman@example.com", "Manly market update — autumn 2026", "market_update", 11, {
        clicked_url: "https://insights.exampleagency.com/suburb/manly",
        country: "Australia",
      }),
    ],
  },
];

export default DEMO_MAP_ADDRESSES;
